import React, { useState } from 'react';
import axios from 'axios';
import './QuestionCreateForm.css';

const QuestionCreateForm = () => {
  const initialQuestionState = {
    text: '',
    type: 'MCQ',
    choices: [],
    correct_answer: '',
    marks: 1,
    difficulty: 0.5,
    discrimination: 1.0,
    explanation: '',
    video: '',
    guessing_parameter: 0.25,
    course: '',
    topics: '',
    image: null
  };

  const [question, setQuestion] = useState(initialQuestionState);
  const [questionList, setQuestionList] = useState([]);
  const [choiceText, setChoiceText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successes, setSuccesses] = useState([]);
  const [failures, setFailures] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuestion(prevQuestion => ({
      ...prevQuestion,
      [name]: value
    }));
  };

  const handleChoiceChange = (e) => {
    setChoiceText(e.target.value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setQuestion(prevQuestion => ({
          ...prevQuestion,
          image: reader.result // Base64 string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addChoice = () => {
    if (choiceText.trim() !== '') {
      setQuestion(prevQuestion => ({
        ...prevQuestion,
        choices: [...prevQuestion.choices, choiceText]
      }));
      setChoiceText('');
    }
  };

  const handleCorrectAnswerChange = (e) => {
    setQuestion(prevQuestion => ({
      ...prevQuestion,
      correct_answer: e.target.value
    }));
  };

  const addQuestionToList = () => {
    setQuestionList([...questionList, question]);
    setQuestion(initialQuestionState);
  };

  const handleSubmitAll = async () => {
    setSubmitting(true);

    const formattedQuestions = questionList.map(q => ({
      ...q,
      choices: q.choices, // Already in the correct format
      topics: q.topics.split(',').map(id => parseInt(id, 10)),
      correct_answer: q.correct_answer
    }));

    try {
      const response = await axios.post('/api/questions/create', formattedQuestions, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      setSuccesses(response.data.successes);
      setFailures(response.data.failures);
      setQuestionList([]);
    } catch (error) {
      console.error('Error creating questions:', error);
      setFailures([...failures, { message: error.message }]);
    }

    setSubmitting(false);
  };

  return (
    <div className="question-form-container">
      <h1 className="form-title">Create a New Question</h1>
      <form className="question-form">
        {/* Question Text */}
        <label>
          Question Text:
          <textarea
            name="text"
            value={question.text}
            onChange={handleInputChange}
            className="form-textarea"
          />
        </label>
        
        {/* Question Type */}
        <label>
          Question Type:
          <select name="type" value={question.type} onChange={handleInputChange} className="form-select">
            <option value="MCQ">Multiple Choice</option>
            <option value="SA">Short Answer</option>
            <option value="MCQA">Multiple Choice Multiple Answers</option>
          </select>
        </label>
        
        {/* Choices */}
        <div>
          <label>
            Choices:
            {question.choices.map((choice, index) => (
              <div key={index}>{choice}</div>
            ))}
          </label>
          <input type="text" value={choiceText} onChange={handleChoiceChange} />
          <button type="button" onClick={addChoice}>Add Choice</button>
        </div>
        
        {/* Correct Answer */}
        <label>
          Correct Answer:
          <select name="correct_answer" value={question.correct_answer} onChange={handleCorrectAnswerChange} className="form-select">
            <option value="">Select the correct answer</option>
            {question.choices.map((choice, index) => (
              <option key={index} value={choice}>{choice}</option>
            ))}
          </select>
        </label>

        {/* Marks */}
        <label>
          Marks:
          <input type="number" name="marks" value={question.marks} onChange={handleInputChange} />
        </label>

        {/* Difficulty */}
        <label>
          Difficulty:
          <input type="number" step="0.01" name="difficulty" value={question.difficulty} onChange={handleInputChange} />
        </label>

        {/* Discrimination */}
        <label>
          Discrimination:
          <input type="number" step="0.01" name="discrimination" value={question.discrimination} onChange={handleInputChange} />
        </label>

        {/* Explanation */}
        <label>
          Explanation:
          <input type="text" name="explanation" value={question.explanation} onChange={handleInputChange} />
        </label>

        {/* Video URL */}
        <label>
          Video URL:
          <input type="url" name="video" value={question.video} onChange={handleInputChange} />
        </label>

        {/* Guessing Parameter */}
        <label>
          Guessing Parameter:
          <input type="number" step="0.01" name="guessing_parameter" value={question.guessing_parameter} onChange={handleInputChange} />
        </label>

        {/* Course ID */}
        <label>
          Course ID:
          <input type="text" name="course" value={question.course} onChange={handleInputChange} />
        </label>

        {/* Topics */}
        <label>
          Topics (comma-separated IDs):
          <input type="text" name="topics" value={question.topics} onChange={handleInputChange} />
        </label>

        {/* Image */}
        <label>
          Image:
          <input type="file" name="image" onChange={handleImageChange} />
        </label>

        <button type="button" onClick={addQuestionToList} className="add-question-button">
          Add Question to List
        </button>
      </form>

      <div className="question-list">
        <h2>Questions to be Submitted:</h2>
        {questionList.map((q, index) => (
          <div key={index} className="listed-question">
            {q.text} - {q.type}
          </div>
        ))}
      </div>

      <button onClick={handleSubmitAll} className="submit-all-button" disabled={submitting || questionList.length === 0}>
        {submitting ? 'Submitting All Questions...' : 'Submit All Questions'}
      </button>

      <div className="form-feedback">
        {successes.length > 0 && (
          <div className="success-messages">
            <h3>Successes:</h3>
            {successes.map((success, index) => (
              <div key={index}>{success.text} created successfully.</div>
            ))}
          </div>
        )}
        {failures.length > 0 && (
          <div className="error-messages">
            <h3>Failures:</h3>
            {failures.map((failure, index) => (
              <div key={index}>{failure.message}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCreateForm;
